import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { UnifiedMapControls } from './components/MapControls';
import logo from './assets/logo.png';

// Fix marker icon issue with Leaflet in some setups
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl:
        'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl:
        'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl:
        'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Create a custom logo icon for zone markers using divIcon with inline styles
const createLogoIcon = (size, borderWidth) => {
    const imgSize = size * 1.3; // Slightly larger to reduce white space but keep logo visible
    return L.divIcon({
        className: 'custom-logo-marker',
        html: `<div style="
            width: ${size}px;
            height: ${size}px;
            border-radius: 50%;
            border: ${borderWidth}px solid #E5B55F;
            box-shadow: 0 2px 8px rgba(229, 181, 95, 0.5);
            overflow: hidden;
            background: white;
            display: flex;
            align-items: center;
            justify-content: center;
        ">
            <img src="${logo}" style="
                width: ${imgSize}px;
                height: ${imgSize}px;
                object-fit: cover;
            " />
        </div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        popupAnchor: [0, -(size / 2)]
    });
};

const zonePinIcon = createLogoIcon(50, 3);

// User location marker with pulsing animation
const userLocationIcon = new L.DivIcon({
    html: `
        <div style="position: relative; width: 40px; height: 40px;">
            <!-- Outer pulsing ring -->
            <div style="
                position: absolute;
                top: 0;
                left: 0;
                width: 40px;
                height: 40px;
                background: rgba(59, 130, 246, 0.4);
                border-radius: 50%;
                animation: pulse 2s ease-out infinite;
            "></div>
            <!-- Middle ring -->
            <div style="
                position: absolute;
                top: 8px;
                left: 8px;
                width: 24px;
                height: 24px;
                background: #3b82f6;
                border: 3px solid white;
                border-radius: 50%;
                box-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
            "></div>
            <!-- Inner core -->
            <div style="
                position: absolute;
                top: 14px;
                left: 14px;
                width: 12px;
                height: 12px;
                background: white;
                border: 2px solid #3b82f6;
                border-radius: 50%;
                box-shadow: 0 0 10px rgba(59, 130, 246, 0.9);
            "></div>
        </div>
        <style>
            @keyframes pulse {
                0% {
                    transform: scale(1);
                    opacity: 0.7;
                }
                50% {
                    transform: scale(1.3);
                    opacity: 0.3;
                }
                100% {
                    transform: scale(1.6);
                    opacity: 0;
                }
            }
        </style>
    `,
    className: '',
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20]
});

// Component for handling flyTo animation and rendering user marker when location available
const UserLocationMarker = ({ position }) => {
    const map = useMap();
    if (position) {
        // Smooth pan/fly animation
        map.flyTo(position, 13, { duration: 1.2 });
        return <Marker position={position} icon={userLocationIcon}><Popup>המיקום שלך</Popup></Marker>;
    }
    return null;
};

// Fit the map to show every zone marker on first render (skipped once the
// user location takes over the view).
const FitToZones = ({ zones, hasUserLocation }) => {
    const map = useMap();
    React.useEffect(() => {
        if (hasUserLocation || !zones || zones.length === 0) return;
        if (zones.length === 1) {
            map.setView([zones[0].lat, zones[0].lng], 13);
        } else {
            map.fitBounds(zones.map(z => [z.lat, z.lng]), { padding: [50, 50], maxZoom: 14 });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zones]);
    return null;
};

// Branded cluster bubble — sized by how many zones it groups.
const createClusterIcon = (cluster) => {
    const count = cluster.getChildCount();
    const size = count < 10 ? 38 : count < 50 ? 46 : 56;
    return L.divIcon({
        className: 'zone-cluster-marker',
        html: `<div style="
            width:${size}px;height:${size}px;border-radius:50%;
            display:flex;align-items:center;justify-content:center;
            background:rgba(229,181,95,0.92);
            border:3px solid #fff;
            box-shadow:0 3px 12px rgba(229,181,95,0.5);
            color:#fff;font-weight:700;
            font-size:${count < 100 ? 15 : 13}px;
            font-family:Rubik, Nunito, Inter, system-ui, sans-serif;
        ">${count}</div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
    });
};

const escapeHtml = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));

// Renders all zone markers inside a leaflet.markercluster group, so nearby
// zones collapse into a single bubble when zoomed out instead of overlapping.
const ClusteredZoneMarkers = ({ zones }) => {
    const map = useMap();
    React.useEffect(() => {
        if (!zones || zones.length === 0) return undefined;

        const group = L.markerClusterGroup({
            chunkedLoading: true,
            maxClusterRadius: 55,
            showCoverageOnHover: false,
            spiderfyOnMaxZoom: true,
            iconCreateFunction: createClusterIcon,
        });

        zones.forEach((zone) => {
            const marker = L.marker([zone.lat, zone.lng], { icon: zonePinIcon });
            const name = escapeHtml(zone.name);
            const routeId = zone.id || zone.name;
            marker.bindPopup(
                `<div style="font-family:Rubik, Nunito, Inter, Varela Round, system-ui, sans-serif;padding:4px 0">
                    <strong style="font-size:16px;color:#222;margin-bottom:6px;display:block">${name}</strong>
                    <a href="?view=zone-details&zone=${encodeURIComponent(routeId)}"
                       style="color:#E5B55F;text-decoration:none;font-size:14px;font-weight:500;display:inline-flex;align-items:center;gap:4px">
                       לעמוד הקהילה →
                    </a>
                </div>`,
                { className: 'zone-popup' }
            );
            group.addLayer(marker);
        });

        map.addLayer(group);
        return () => { map.removeLayer(group); };
    }, [zones, map]);

    return null;
};

export const ZoneMap = ({ zones, chatId, userLocation }) => {
    // Default center (Tel Aviv) if no zones yet
    const center = zones && zones.length
        ? [zones[0].lat, zones[0].lng]
        : [32.08, 34.78];

    return (
        <MapContainer
            center={center}
            zoom={10}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
            scrollWheelZoom={true}
        >
            {/* Exact same basemap as the zone-details map (ZoneMapHero) —
                CARTO Positron renders local-language (Hebrew) place names. */}
            <TileLayer
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                attribution="&copy; OpenStreetMap &copy; CARTO"
            />

            <FitToZones zones={zones} hasUserLocation={!!userLocation} />

            <ClusteredZoneMarkers zones={zones} />
            <UserLocationMarker position={userLocation} />
            <UnifiedMapControls userLocation={userLocation} />
        </MapContainer>
    );
};
